import { Component, Inject, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CamapignService } from '../../services/campaign.service';
import { CamapignScanService } from '../../services/campaign-scan.service';
import { Camapign, CamapignScans } from '../../interfaces/campaigns.interface';
import { GenericResponseWithTotal } from '../../interfaces/generic.interface';
import { CameraService } from '../../services/camera.service';

@Component({
  selector: 'app-campaign-scans',
  standalone: false,
  templateUrl: './campaign-scans.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-scans.component.scss']
})
export class CampaignScansComponent implements OnInit {

  // Campaigns list
  campaigns: Camapign[] = [];
  campaignsLoading = false;
  campaignsError: string | null = null;
  displayedCampaignColumns = ['active', 'name', 'valid', 'scans'];

  // Expanded campaign
  expandedCampaignId: number | null = null;
  dialogCampaignName: string | null = null;

  get isDialogMode(): boolean {
    return !!this.dialogData?.campaign;
  }

  // Scans sub-table
  scans: CamapignScans[] = [];
  scansTotal = 0;
  scansLoading = false;
  scansError: string | null = null;
  displayedScanColumns = ['person_name', 'user_name', 'type', 'name', 'score', 'amount', 'created'];

  // Pagination
  page = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Filters
  filterText = '';
  filterType = '';
  filterDateFromDate: any = null;
  filterDateToDate: any = null;

  private formatDate(d: any): string {
    if (!d) return '';
    // MatMomentDateAdapter returns a Moment object
    if (typeof d.format === 'function') return d.format('YYYY-MM-DD');
    // Fallback for native Date
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  scanTypeOptions = [
    { value: '', label: 'Wszystkie' },
    { value: 'entry', label: 'Wejście' },
    { value: 'transaction', label: 'Transakcja' },
  ];

  constructor(
    private campaignService: CamapignService,
    private scanService: CamapignScanService,
    private cameraService: CameraService,
    @Optional() public dialogRef: MatDialogRef<CampaignScansComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { campaign: Camapign }
  ) {}

  ngOnInit(): void {
    if (this.isDialogMode) {
      this.expandedCampaignId = this.dialogData.campaign.id;
      this.dialogCampaignName = this.dialogData.campaign.name;
      this.loadScans();
    } else {
      this.loadCampaigns();
    }
  }

  loadCampaigns(): void {
    this.campaignsLoading = true;
    this.campaignsError = null;
    this.campaignService.getPartnersCamaigns().subscribe({
      next: (res: GenericResponseWithTotal<Camapign>) => {
        this.campaigns = res.items ?? (res as any);
        this.campaignsLoading = false;
      },
      error: () => {
        this.campaignsError = 'Nie udało się załadować kampanii.';
        this.campaignsLoading = false;
      }
    });
  }

  toggleScans(campaign: Camapign): void {
    if (this.expandedCampaignId === campaign.id) {
      this.expandedCampaignId = null;
      this.scans = [];
      this.scansTotal = 0;
    } else {
      this.expandedCampaignId = campaign.id;
      this.page = 0;
      this.filterText = '';
      this.filterType = '';
      this.filterDateFromDate = null;
      this.filterDateToDate = null;
      this.loadScans();
    }
  }

  loadScans(): void {
    if (this.expandedCampaignId === null) return;
    this.scansLoading = true;
    this.scansError = null;

    const params: any = {
      campaign_id: this.expandedCampaignId,
      page: this.page + 1,
      per_page: this.pageSize,
    };
    if (this.filterText) params['search'] = this.filterText;
    if (this.filterType) params['type'] = this.filterType;
    const dateFrom = this.formatDate(this.filterDateFromDate);
    const dateTo = this.formatDate(this.filterDateToDate);
    if (dateFrom) params['date_from'] = dateFrom;
    if (dateTo) params['date_to'] = dateTo;

    this.scanService.getScans(params).subscribe({
      next: (res: GenericResponseWithTotal<CamapignScans>) => {
        this.scans = res.items ?? (res as any);
        this.scansTotal = res.total ?? this.scans.length;
        this.scansLoading = false;
      },
      error: () => {
        this.scansError = 'Nie udało się załadować logów skanów.';
        this.scansLoading = false;
      }
    });
  }

  applyFilter(): void {
    this.page = 0;
    this.loadScans();
  }

  clearFilters(): void {
    this.filterText = '';
    this.filterType = '';
    this.filterDateFromDate = null;
    this.filterDateToDate = null;
    this.page = 0;
    this.loadScans();
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadScans();
  }

  async checkCameraSupport(): Promise<boolean> {
    return await this.cameraService.checkCameraSupport();
  }

  async startCameraForWeb(): Promise<void> {
    if (this.cameraService.isNativePlatform()) {
      // Use native camera plugin
      const dataUrl = await this.cameraService.takePhoto();
      // Process image for QR code
      console.log('Photo taken:', dataUrl);
    } else {
      // Use getUserMedia for web
      const stream = await this.cameraService.getCameraStream({ video: true });
      // Your existing web implementation
      console.log('Stream obtained:', stream);
    }
  }
}

